import { ScalingTypes, DialogTab } from '../autoscaling-form-field/autoscaling-form.model';
import { ServiceStackHorizontalAutoscaling, ServiceStackVerticalAutoscaling, } from '@zerops/models/settings';
import { AutoscalingProfile } from '@zerops/models/service-stack';
import { ScalingDialogData } from '../autoscaling-form-field/autoscaling-form.model';

/**
 * Effective placeholders displayed for autoscaling inputs.
 *
 * - `defaults` comes from `config.defaultAutoscaling` — pre-merged defaults the BE computes from
 *   the default profile. Shown as-is when no profile is selected.
 * - `base` comes from `config.verticalAutoscaling` / `config.horizontalAutoscaling` — the raw
 *   service-type values. Used as the overlay target when any profile is selected, because
 *   `defaults` already has the default profile baked in and would leak into other profiles.
 */
export function resolveEffectivePlaceholders(
  defaults: {
    vertical: ServiceStackVerticalAutoscaling | undefined;
    horizontal: ServiceStackHorizontalAutoscaling | undefined;
  },
  base: {
    vertical: ServiceStackVerticalAutoscaling | undefined;
    horizontal: ServiceStackHorizontalAutoscaling | undefined;
  },
  profile: AutoscalingProfile | null | undefined
): {
  vertical: ServiceStackVerticalAutoscaling | undefined;
  horizontal: ServiceStackHorizontalAutoscaling | undefined;
} {
  if (!profile) {
    return { vertical: defaults.vertical, horizontal: defaults.horizontal };
  }

  const profileVertical = profile.customAutoscaling?.verticalAutoscaling;
  const profileHorizontal = profile.customAutoscaling?.horizontalAutoscaling;

  const vertical = profileVertical
    ? ({
      ...base.vertical,
      minResource: { ...base.vertical?.minResource, ...filterNulls(profileVertical.minResource) },
      maxResource: { ...base.vertical?.maxResource, ...filterNulls(profileVertical.maxResource) },
      ...(profileVertical.cpuMode != null && { cpuMode: profileVertical.cpuMode }),
      ...(profileVertical.startCpuCoreCount != null && { startCpuCoreCount: profileVertical.startCpuCoreCount }),
      ...(profileVertical.minFreeResource && {
        minFreeResource: { ...base.vertical?.minFreeResource, ...filterNulls(profileVertical.minFreeResource) }
      })
    } as ServiceStackVerticalAutoscaling)
    : base.vertical;

  const horizontal = profileHorizontal
    ? ({ ...base.horizontal, ...filterNulls(profileHorizontal) } as ServiceStackHorizontalAutoscaling)
    : base.horizontal;

  return { vertical, horizontal };
}

function filterNulls(obj: any): Record<string, any> {
  if (!obj) return {};
  return Object.entries(obj).reduce((acc, [ key, value ]) => {
    if (value != null) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);
}

/**
 * Container (horizontal) scaling is available only when limits exist AND at least one bound
 * (min or max container count) is a positive value. A config with both bounds null/0 — or no
 * `horizontalAutoscaling` at all — has no meaningful range to configure, so the Containers
 * tab and form are hidden. A config like `{ minContainerCount: 0, maxContainerCount: 10 }`
 * still counts as available because the max bound is positive.
 */
export function isContainerScalingAvailable(
  limits: ServiceStackHorizontalAutoscaling | undefined | null
): boolean {
  return !!limits && (!!limits.minContainerCount || !!limits.maxContainerCount);
}

// Static configurations
export const SCALING_TYPE_SUFFIX_MAP = {
  [ScalingTypes.Cpu]: 'Cores',
  [ScalingTypes.Disc]: 'GB',
  [ScalingTypes.Ram]: 'GB'
};

export const SCALING_BUTTON_VALUES = {
  [ScalingTypes.Container]: {
    buttons: [ 1 ]
  },
  [ScalingTypes.Cpu]: {
    buttons: [ 1 ]
  },
  [ScalingTypes.Disc]: {
    buttons: [ 0.5, 5 ]
  },
  [ScalingTypes.Ram]: {
    buttons: [ 0.125, 0.5 ]
  },
};

export interface ScalingValues {
  container: {
    min: number;
    max: number;
  };
  cpu: {
    min: number;
    max: number;
    minFree: number; // decimal number
    minFreeNum: number; // whole number (helper)
    minFreePct: number;
    startCount: number;
  };
  ram: {
    min: number;
    max: number;
    minFree: number;
    minFreePct: number;
  };
  disc: {
    min: number;
    max: number;
  };
}

/**
 * Extracts the current scaling values from the currentData object.
 * This data represents real values defined by the user.
 * @param currentData
 */
export function extractScalingValues(currentData: any): ScalingValues {
  return {
    container: {
      min: currentData?.horizontalAutoscaling?.minContainerCount,
      max: currentData?.horizontalAutoscaling?.maxContainerCount
    },
    cpu: {
      min: currentData?.verticalAutoscaling?.minResource?.cpuCoreCount,
      max: currentData?.verticalAutoscaling?.maxResource?.cpuCoreCount,
      // whole number (transform to decimal whether is numeric value)
      minFree: !!currentData?.verticalAutoscaling?.minFreeResource?.cpuCoreCount
        ? currentData?.verticalAutoscaling?.minFreeResource?.cpuCoreCount * 100
        : currentData?.verticalAutoscaling?.minFreeResource?.cpuCoreCount,
      // decimal number
      minFreeNum: currentData?.verticalAutoscaling?.minFreeResource?.cpuCoreCount,
      minFreePct: currentData?.verticalAutoscaling?.minFreeResource?.cpuCorePercent,
      startCount: currentData?.verticalAutoscaling?.startCpuCoreCount
    },
    ram: {
      min: currentData?.verticalAutoscaling?.minResource?.memoryGBytes,
      max: currentData?.verticalAutoscaling?.maxResource?.memoryGBytes,
      minFree: currentData?.verticalAutoscaling?.minFreeResource?.memoryGBytes,
      minFreePct: currentData?.verticalAutoscaling?.minFreeResource?.memoryPercent
    },
    disc: {
      min: currentData?.verticalAutoscaling?.minResource?.diskGBytes,
      max: currentData?.verticalAutoscaling?.maxResource?.diskGBytes
    }
  };
}

export function createScalingConfiguration(
  type: DialogTab | undefined,
  values: ScalingValues,
  verticalPlaceholder: ServiceStackVerticalAutoscaling,
  horizontalPlaceholder: ServiceStackHorizontalAutoscaling,
): ScalingDialogData {
  return {
    type,
    [ScalingTypes.Container]: {
      min: values.container.min,
      max: values.container.max,
      defaultMin: horizontalPlaceholder?.minContainerCount, // placeholder
      defaultMax: horizontalPlaceholder?.maxContainerCount // placeholder
    },
    [ScalingTypes.Cpu]: {
      min: values.cpu.min,
      max: values.cpu.max,
      minFree: values.cpu.minFree, // whole number (24%)
      minFreeNum: values.cpu.minFree, // decimal number (0.24)
      minFreePct: values.cpu.minFreePct,
      startCount: values.cpu.startCount,
      defaultMin: verticalPlaceholder?.minResource?.cpuCoreCount, // placeholder
      defaultMax: verticalPlaceholder?.maxResource?.cpuCoreCount, // placeholder
      defaultStartCount: verticalPlaceholder?.startCpuCoreCount, // placeholder
      defaultMinFree: verticalPlaceholder?.minFreeResource?.cpuCoreCount, // placeholder
      defaultMinFreePct: verticalPlaceholder?.minFreeResource?.cpuCorePercent, // placeholder
    },
    [ScalingTypes.Ram]: {
      min: values.ram.min,
      max: values.ram.max,
      minFree: values.ram.minFree,
      minFreePct: values.ram.minFreePct,
      defaultMin: verticalPlaceholder?.minResource?.memoryGBytes, // placeholder
      defaultMax: verticalPlaceholder?.maxResource?.memoryGBytes, // placeholder
      defaultMinFree: verticalPlaceholder?.minFreeResource?.memoryGBytes, // placeholder
      defaultMinFreePct: verticalPlaceholder?.minFreeResource?.memoryPercent, // placeholder
    },
    [ScalingTypes.Disc]: {
      min: values.disc.min,
      max: values.disc.max,
      defaultMin: verticalPlaceholder?.minResource?.diskGBytes, // placeholder
      defaultMax: verticalPlaceholder?.maxResource?.diskGBytes, // placeholder
    }
  };
}

/**
 * Returns true when the values are logically inconsistent. Over-limit values are stored as-is;
 * BE clamps each value into `[minLimit, maxLimit]` before applying, so the FE mirrors that and
 * only flags a conflict when the *clamped* min still exceeds the *clamped* max. This lets a
 * profile that sets e.g. minCPU=4 coexist with a client limit of 3 (both clamp to 3 → valid).
 */
export function checkLimits(
  data: { min: number | null; max: number | null, minLimit: number, maxLimit: number },
  isSingle: boolean
): boolean {
  const { min, max, minLimit, maxLimit } = data;

  if (typeof min !== 'number' || typeof max !== 'number') {
    return true;
  }

  if (isSingle) return false;

  const clampable = typeof minLimit === 'number' && typeof maxLimit === 'number';
  const clampedMin = clampable ? Math.min(Math.max(min, minLimit), maxLimit) : min;
  const clampedMax = clampable ? Math.min(Math.max(max, minLimit), maxLimit) : max;

  if (clampedMin > clampedMax) return true;

  return false;
}

export interface OverLimitField {
  scalingType: ScalingTypes;
  bound: 'min' | 'max';
  label: string;
  entered: number;
  limit: number;
  suffix: string;
}

/**
 * Returns all min/max fields whose effective value (user-entered or profile-derived) falls
 * outside the client's current limits. `cpuRamFixedMode` means CPU and RAM are single-value,
 * so only the max field is inspected for those two.
 */
export function computeOverLimitFields(opts: {
  value: any;
  verticalPlaceholder: ServiceStackVerticalAutoscaling | undefined;
  horizontalPlaceholder: ServiceStackHorizontalAutoscaling | undefined;
  verticalLimits: ServiceStackVerticalAutoscaling | undefined;
  horizontalLimits: ServiceStackHorizontalAutoscaling | undefined;
  cpuRamFixedMode: boolean;
}): OverLimitField[] {
  const { value, verticalPlaceholder, horizontalPlaceholder, verticalLimits, horizontalLimits, cpuRamFixedMode } = opts;
  const fields: OverLimitField[] = [];

  const push = (
    field: { scalingType: ScalingTypes; bound: 'min' | 'max'; label: string; suffix: string; },
    entered: number | undefined,
    minLimit: number | undefined,
    maxLimit: number | undefined
  ) => {
    if (typeof entered !== 'number') return;
    // A value is "outside current limits" when it falls outside [minLimit, maxLimit] in either
    // direction — e.g. a profile's minCPU=4 on a client with maxLimit=3 is flagged because
    // entered > maxLimit, even though it's a min-bound field.
    if (typeof minLimit === 'number' && entered < minLimit) {
      fields.push({ ...field, entered, limit: minLimit });
    } else if (typeof maxLimit === 'number' && entered > maxLimit) {
      fields.push({ ...field, entered, limit: maxLimit });
    }
  };

  if (horizontalLimits) {
    const hMin = value?.horizontalAutoscaling?.minContainerCount ?? horizontalPlaceholder?.minContainerCount;
    const hMax = value?.horizontalAutoscaling?.maxContainerCount ?? horizontalPlaceholder?.maxContainerCount;
    push(
      { scalingType: ScalingTypes.Container, bound: 'min', label: 'Minimum containers', suffix: '' },
      hMin, horizontalLimits.minContainerCount, horizontalLimits.maxContainerCount
    );
    push(
      { scalingType: ScalingTypes.Container, bound: 'max', label: 'Maximum containers', suffix: '' },
      hMax, horizontalLimits.minContainerCount, horizontalLimits.maxContainerCount
    );
  }

  if (verticalLimits) {
    const vMinRes = value?.verticalAutoscaling?.minResource;
    const vMaxRes = value?.verticalAutoscaling?.maxResource;
    const vPlaceholderMin = verticalPlaceholder?.minResource;
    const vPlaceholderMax = verticalPlaceholder?.maxResource;

    const cpuMin = vMinRes?.cpuCoreCount ?? vPlaceholderMin?.cpuCoreCount;
    const cpuMax = vMaxRes?.cpuCoreCount ?? vPlaceholderMax?.cpuCoreCount;
    if (!cpuRamFixedMode) {
      push(
        { scalingType: ScalingTypes.Cpu, bound: 'min', label: 'Minimum CPU cores', suffix: 'cores' },
        cpuMin, verticalLimits.minResource?.cpuCoreCount, verticalLimits.maxResource?.cpuCoreCount
      );
    }
    push(
      { scalingType: ScalingTypes.Cpu, bound: 'max', label: cpuRamFixedMode ? 'CPU cores' : 'Maximum CPU cores', suffix: 'cores' },
      cpuMax, verticalLimits.minResource?.cpuCoreCount, verticalLimits.maxResource?.cpuCoreCount
    );

    const ramMin = vMinRes?.memoryGBytes ?? vPlaceholderMin?.memoryGBytes;
    const ramMax = vMaxRes?.memoryGBytes ?? vPlaceholderMax?.memoryGBytes;
    if (!cpuRamFixedMode) {
      push(
        { scalingType: ScalingTypes.Ram, bound: 'min', label: 'Minimum memory', suffix: 'GB' },
        ramMin, verticalLimits.minResource?.memoryGBytes, verticalLimits.maxResource?.memoryGBytes
      );
    }
    push(
      { scalingType: ScalingTypes.Ram, bound: 'max', label: cpuRamFixedMode ? 'Memory' : 'Maximum memory', suffix: 'GB' },
      ramMax, verticalLimits.minResource?.memoryGBytes, verticalLimits.maxResource?.memoryGBytes
    );

    const diskMin = vMinRes?.diskGBytes ?? vPlaceholderMin?.diskGBytes;
    const diskMax = vMaxRes?.diskGBytes ?? vPlaceholderMax?.diskGBytes;
    push(
      { scalingType: ScalingTypes.Disc, bound: 'min', label: 'Minimum disk', suffix: 'GB' },
      diskMin, verticalLimits.minResource?.diskGBytes, verticalLimits.maxResource?.diskGBytes
    );
    push(
      { scalingType: ScalingTypes.Disc, bound: 'max', label: 'Maximum disk', suffix: 'GB' },
      diskMax, verticalLimits.minResource?.diskGBytes, verticalLimits.maxResource?.diskGBytes
    );
  }

  return fields;
}
